import { Component, OnInit } from '@angular/core';
import {
	AbstractControl,
	FormControl,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import {
	EProfileBillingStatus,
	IProfessionalPerson,
	IProfessionalProfile,
} from 'app/interfaces/professionalprofile.interface';
import { IPlan, ProfessionalPlan } from 'app/interfaces/subscription.interface';
import { SubscriptionService } from 'app/services/subscription.service';
import { UserService } from 'app/services/user.service';

import { ActionTrackerService } from './../../services/actiontracker.service';
import {
	ISubscription,
	PaymentMethod,
	ProfessionalSubscription,
	ProfessionalSubscriptionResponse,
	ProfessionalSubscriptionStatus,
} from '../../interfaces/subscription.interface';
import { CurrencyService } from '../../services/currency.service';
import { DateTimeService } from '../../services/datetime.service';
import { GlobalService } from '../../services/global.service';
import config from './config.json';

@Component({
	selector: 'app-account-premium',
	templateUrl: './premium.component.html',
	styleUrls: ['./premium.component.scss'],
})
export class AccountPremiumComponent implements OnInit {
	benefits: string[] = config.benefits;
	captions = config.captions;

	plans: IPlan[] = [];
	professionalPlans: ProfessionalPlan[] = [];

	subscriptions: ISubscription[] = config.subscriptions;
	professionalSubscriptions: ProfessionalSubscription[] = [];
	yourSubscription = config.yourSubscription;
	lastPaymentMethod: PaymentMethod;
	cancellationDescription: FormControl;

	modalCancellation: {
		details: any;
		currentStep: number;
		isOpen: boolean;
		isLoading: boolean;
		reasonIndex: number;
	};
	isModalFinancialOpened: boolean;
	isLoading = true;
	buttonLoading = false;
	isMobile = false;
	isButtonLoading = false;
	invalidReason = false;

	userData: IProfessionalProfile;
	professionalPerson: IProfessionalPerson;
	enableCancelationStates: ProfessionalSubscriptionStatus[] = [
		'active',
		'trialing',
	];

	enableChangePlanStates: ProfessionalSubscriptionStatus[] = ['incomplete'];

	enableRepayStates: ProfessionalSubscriptionStatus[] = [
		'incomplete',
		'unpaid',
		'incomplete_expired',
	];

	enableRenewStates: ProfessionalSubscriptionStatus[] = ['trialing'];

	enableUndoCancelationStates: ProfessionalSubscriptionStatus[] = [
		'requested_cancelation',
		'cancelation_accepted',
	];

	enableUpdatePaymentMethod: ProfessionalSubscriptionStatus = 'past_due';

	get hasPVProfessionalTag() {
		return this.userData?.tags?.find((tag) => tag.id === 'professional_pv');
	}

	constructor(
		public currencyService: CurrencyService,
		protected dateTimeService: DateTimeService,
		protected globalService: GlobalService,
		protected notifications: ZkNotificationService,
		protected router: Router,
		protected subscriptionService: SubscriptionService,
		protected translateService: TranslateService,
		protected userService: UserService,
		private atService: ActionTrackerService
	) {}

	ngOnInit() {
		this.cancellationDescription = new FormControl('', [
			Validators.required,
			Validators.minLength(5),
			(control: AbstractControl): ValidationErrors | null => {
				const regex = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
				const specialCharacters = regex.test(control.value || '');
				if (specialCharacters) {
					return { hasSpecialCharacters: true };
				}
			},
			(control: AbstractControl): ValidationErrors | null => {
				const value = control.value;
				if (value && value.split('').every((char) => char === value[0])) {
					return { allCharactersIsEqual: true };
				}
			},
		]);

		this.setCancelationModal(false);
		this.onResize();
		if (this.isMobile) {
			this.getUserImage();
		}
		this.getProfessionalPerson();
		this.getUserProfile();
		this.getSubscriptions();
		this.getPlans();
	}

	getUserImage() {
		this.userData = {
			imageUrl: this.globalService.getDefaultUserPicture(),
		} as IProfessionalProfile;
	}

	getUserProfile() {
		this.userService.getProfileData().then((userData) => {
			this.userData = userData;
			this.atService.eventTracker(
				SCOPE_TYPE.Account,
				SUBSCOPE_TYPE.Plan,
				ITEM_TYPE.Premium,
				INTERACTION_TYPE.Seen,
				{
					specialist_type: this.userData.mainType,
				}
			);
		});
	}

	getSubscriptions() {
		this.subscriptionService.getSubscriptionList().subscribe(
			(response: ProfessionalSubscriptionResponse) => {
				this.professionalSubscriptions = response.subscriptions.filter(
					(sub: ProfessionalSubscription) =>
						sub.status != 'unpaid' &&
						sub.status != 'canceled' &&
						sub.status != 'incomplete_expired'
				);
				this.professionalSubscriptions = this.professionalSubscriptions.map(
					(s) => {
						s.status =
							this.subscriptionStatusIs(this.enableCancelationStates) &&
							s.canceledAt
								? 'requested_cancelation'
								: s.status;
						return s;
					}
				);
				this.lastPaymentMethod = response.lastPaymentMethod;
				this.isLoading = false;

				if (this.lastPaymentMethod) {
					this.subscriptionService.addToDataLayer(response);
				}
			},
			() => {
				this.notifications.showError(
					this.translateService.instant(
						'PAGES.MEMBERSHIP.MESSAGES.GET_SUBSCRIPTIONS_ERROR'
					),
					this.translateService.instant('PAGES.MEMBERSHIP.MESSAGES.ERROR')
				);
				this.isLoading = false;
			}
		);
	}

	getPlans() {
		this.subscriptionService
			.getPlanList()
			.subscribe((response: ProfessionalPlan[]) => {
				this.professionalPlans = response;

				this.plans = response.map((plan: ProfessionalPlan) => {
					return {
						...(plan as any),
						monthlyPrice: Math.ceil(plan.price / plan.installments),
						selected: plan.period.period === 'year',
					};
				});

				const prices = this.plans.map((plan: IPlan) => {
					switch (plan.period.period) {
						case 'month':
							return (12 / plan.installments) * plan.price;
						case 'year':
							return plan.price;
					}
				});
				const min = Math.min(...prices);
				const max = Math.max(...prices);
				const economy = `${Math.ceil(((min * 100) / max - 100) * -1)}%`;
				this.plans.find((plan: IPlan) => plan.price === min).flag = economy;
			});
	}

	goToCheckout(planId?: ProfessionalPlan['id']) {
		if (this.isMissingFinancialProfileActive() || this.isNaturalPerson()) {
			this.isModalFinancialOpened = true;
			this.buttonLoading = false;
			return;
		}
		this.router.navigate(['/checkout/'], {
			queryParams: { pid: planId || this.getSubscription().planId },
			state: { returnTo: '/account/premium' },
		});
	}

	isMissingFinancialProfileActive() {
		const currentStatus = this.professionalPerson?.status;
		const {
			Active,
			ChangeRequested,
			ChangeAccepted,
			UpdateRequested,
			WaitingChange,
		} = EProfileBillingStatus;
		const status = [
			Active,
			ChangeRequested,
			ChangeAccepted,
			UpdateRequested,
			WaitingChange,
		];
		return !status.includes(currentStatus);
	}

	isNaturalPerson() {
		const naturalPerson =
			this.professionalPerson?.type === 'legal' ? false : true;
		return naturalPerson;
	}

	onResize() {
		this.isMobile = this.globalService.isMobileDevice();
	}

	getSubscriptionClass(plan: string): string {
		return `box-${plan.toLowerCase()}`;
	}

	typeOf(value: any): string {
		return typeof value;
	}

	checkPlanOption(index: number) {
		this.plans.forEach((e: IPlan) => (e.selected = false));
		this.plans[index].selected = true;
	}

	signPlan() {
		this.buttonLoading = true;
		const plan = this.plans.filter((plan: IPlan) => plan.selected)[0];
		this.goToCheckout(plan.id);
	}

	hasSubscription(): boolean {
		return (
			this.professionalSubscriptions &&
			this.professionalSubscriptions.length > 0
		);
	}

	// Get the latest active subscription (discarding canceled and unpaid)
	getSubscription(): ProfessionalSubscription {
		if (!this.hasSubscription()) {
			return null;
		}

		const subs = this.professionalSubscriptions;
		return subs[subs.length - 1];
	}

	getYourSubscriptionData(item: string): string {
		const subscription = this.getSubscription();

		switch (item) {
			case 'STATUS':
				const status =
					subscription.status === 'trialing' &&
					this.lastPaymentMethod === 'boleto'
						? 'active'
						: subscription.status;
				return this.getStatus(status);
			case 'FREQUENCY':
				return subscription.plan.name;
			case 'BILLING_METHOD':
				return this.translateService.instant(
					`PAGES.YOUR_SUBSCRIPTION.TABLE.PAYMENT_TYPE_CODE.${this.lastPaymentMethod.toUpperCase()}`
				);
			case 'NEXT_BILLING_DATE':
				if (subscription.currentPeriodEndsAt) {
					return this.dateTimeService.getInFullDate(
						new Date(subscription.currentPeriodEndsAt)
					);
				} else {
					return '';
				}
			case 'RENOVATION':
				if (subscription.currentPeriodEndsAt) {
					return this.dateTimeService.getInFullDate(
						new Date(subscription.currentPeriodEndsAt)
					);
				} else {
					return '';
				}
			case 'VALUE':
				const symbol = this.currencyService.getCurrencySymbol(
					subscription.plan.currency
				);
				return `${symbol} ${subscription.plan.price}`;
		}
	}

	getStatus(status: string): string {
		return this.translateService.instant(
			`PAGES.YOUR_SUBSCRIPTION.STATUS.${status.toUpperCase()}`
		);
	}

	setCancelationModal(isOpen = true) {
		this.invalidReason = false;
		this.modalCancellation = {
			details: config.modals.find((modal) => modal.name === 'CANCELATION'),
			currentStep: 1,
			isOpen: isOpen,
			isLoading: false,
			reasonIndex: undefined,
		};
	}

	showCancelModal() {
		if (this.subscriptionStatusIs(this.enableCancelationStates)) {
			this.cancellationDescription.patchValue('');
			this.cancellationDescription.markAsPristine();
			this.cancellationDescription.markAsUntouched();
			this.atService.eventTracker(
				SCOPE_TYPE.Account,
				SUBSCOPE_TYPE.Plan,
				ITEM_TYPE.Cancelation,
				INTERACTION_TYPE.Start,
				{
					specialist_type: this.userData.mainType,
				}
			);
			this.setCancelationModal();
		}
	}

	undoCancelation() {
		const subscription = this.getSubscription();
		this.isButtonLoading = true;

		this.subscriptionService.undoCancelation(subscription.id).subscribe(
			() => {
				this.isLoading = true;
				this.isButtonLoading = false;
				this.getSubscriptions();
				this.notifications.showSuccess(
					this.translateService.instant(
						'PAGES.YOUR_SUBSCRIPTION.UNDO_CANCELLED_MESSAGE'
					)
				);
				this.atService.eventTracker(
					SCOPE_TYPE.Account,
					SUBSCOPE_TYPE.Plan,
					ITEM_TYPE.Cancelation,
					INTERACTION_TYPE.Undo,
					{
						specialist_type: this.userData.mainType,
					}
				);
			},
			() => {
				this.isButtonLoading = false;
				this.notifications.showError(
					this.translateService.instant(
						'PAGES.MEMBERSHIP.MESSAGES.UNDO_CANCELATION'
					),
					this.translateService.instant('PAGES.MEMBERSHIP.MESSAGES.ERROR')
				);
			}
		);
	}

	cancelSubscription() {
		const subscription = this.getSubscription();
		const data = { reasons: [] };

		this.modalCancellation.isLoading = true;
		data.reasons.push(this.cancellationDescription.value);

		this.subscriptionService
			.cancelSubscription(subscription.id, data)
			.subscribe(
				() => {
					this.modalCancellation.isLoading = false;
					this.subscriptionService.trackSubscriptionCancellation(
						subscription.professionalId,
						this.userData.email,
						data.reasons
					);
					this.atService.eventTracker(
						SCOPE_TYPE.Account,
						SUBSCOPE_TYPE.Plan,
						ITEM_TYPE.Cancelation,
						INTERACTION_TYPE.Requested,
						{
							specialist_type: this.userData.mainType,
							selected: this.getReasonForTracker(
								this.modalCancellation.reasonIndex
							),
						}
					);
					this.modalCancellation.currentStep = 3;
				},
				() => {
					this.modalCancellation.isLoading = false;
					this.isLoading = false;
					this.notifications.showError(
						this.translateService.instant(
							'PAGES.MEMBERSHIP.MESSAGES.CANCELATION_ERROR'
						),
						this.translateService.instant('PAGES.MEMBERSHIP.MESSAGES.ERROR')
					);
					this.atService.eventTracker(
						SCOPE_TYPE.Account,
						SUBSCOPE_TYPE.Plan,
						ITEM_TYPE.Cancelation,
						INTERACTION_TYPE.Error,
						{
							specialist_type: this.userData.mainType,
							selected: this.getReasonForTracker(
								this.modalCancellation.reasonIndex
							),
						}
					);
				}
			);
	}

	getReasonErrorMessage() {
		if (!this.cancellationDescription?.errors) {
			return;
		}
		if (this.cancellationDescription.errors.required) {
			return this.translateService.instant(
				'ERRORS.PREMIUM_CANCELLATION.REQUIRED'
			);
		} else if (this.cancellationDescription.errors.minlength) {
			return this.translateService.instant(
				'ERRORS.PREMIUM_CANCELLATION.MIN_LENGTH'
			);
		} else if (this.cancellationDescription.errors.hasSpecialCharacters) {
			return this.translateService.instant(
				'ERRORS.PREMIUM_CANCELLATION.SPECIAL_CHARACTERS'
			);
		} else if (this.cancellationDescription.errors.allCharactersIsEqual) {
			return this.translateService.instant(
				'ERRORS.PREMIUM_CANCELLATION.ALL_EQUAL'
			);
		}
	}

	subscriptionStatusIs(
		status: ProfessionalSubscriptionStatus | ProfessionalSubscriptionStatus[]
	) {
		const subscription = this.getSubscription();

		const sts: ProfessionalSubscriptionStatus[] =
			typeof status === 'string' ? [status] : status;
		const subStatus = subscription.status.toLowerCase();
		return sts.some(
			(s: ProfessionalSubscriptionStatus) =>
				s.toLowerCase() === subStatus.toLowerCase()
		);
	}

	onContinueCancellationDialog() {
		switch (this.modalCancellation.currentStep) {
			case 1:
				if (this.isModalStep1Disabled()) {
					return;
				}
				this.modalCancellation.currentStep += 1;
				if (!this.cancellationDescription.value) {
					this.cancellationDescription.patchValue(
						this.modalCancellation.details['STEP_1']['ITEMS'][
							this.modalCancellation.reasonIndex
						]
					);
				}
				break;
			case 2:
				this.cancelSubscription();
				break;
			case 3:
				this.setCancelationModal(false);
				this.isLoading = true;
				this.getSubscriptions();
		}
	}

	isReasonInvalid(): boolean {
		const field = this.cancellationDescription;
		if (field) {
			return field.invalid && (field.dirty || field.touched);
		}
		return false;
	}

	isModalStep1Disabled() {
		const { reasonIndex } = this.modalCancellation;
		const othersIndex =
			this.modalCancellation.details['STEP_1']['ITEMS'].length;
		if (reasonIndex === othersIndex) {
			return this.cancellationDescription.invalid;
		}
		return !reasonIndex && reasonIndex !== 0;
	}

	getProfessionalPerson() {
		this.userService
			.checkProfessionalPerson()
			.subscribe((professionalPerson) => {
				this.professionalPerson = professionalPerson;
			});
	}

	goToPaymentAccount() {
		this.isModalFinancialOpened = false;
		this.router.navigate(['/account/payment']);
	}

	goToStripeCustomerPortal() {
		window.open(
			`https://billing.stripe.com/p/login/fZe3ff5Z59Mwe6Q7ss?prefilled_email=${this.userData?.email}`
		);
	}

	trackRequestCancel() {
		this.atService.eventTracker(
			SCOPE_TYPE.Account,
			SUBSCOPE_TYPE.Plan,
			ITEM_TYPE.Cancelation,
			INTERACTION_TYPE.Cancel,
			{
				specialist_type: this.userData.mainType,
				selected: this.getReasonForTracker(this.modalCancellation.reasonIndex),
			}
		);
	}

	trackContinue() {
		this.atService.eventTracker(
			SCOPE_TYPE.Account,
			SUBSCOPE_TYPE.Plan,
			ITEM_TYPE.Cancelation,
			INTERACTION_TYPE.Continue,
			{
				specialist_type: this.userData.mainType,
				selected: this.getReasonForTracker(this.modalCancellation.reasonIndex),
			}
		);
	}

	getReasonForTracker(i) {
		return this.modalCancellation.reasonIndex === 4
			? 'Outros'
			: config.modals[0].STEP_1.ITEMS[i];
	}
}
